<template>
	<div class="overlay-layer-content">
		<img v-if="layer.type === 'image' && layer.props.src" :src="layer.props.src" :style="mediaCropStyle" draggable="false" />
		<div v-else-if="layer.type === 'image'" class="overlay-layer-placeholder"><b-icon icon="image" /></div>
		<!-- Always muted here: the editor is a preview, and sound belongs to the OBS source. -->
		<video v-else-if="layer.type === 'video' && layer.props.src" ref="video" :src="layer.props.src" :style="mediaCropStyle" :muted="true" :loop="layer.props.loop !== false" playsinline draggable="false" />
		<div v-else-if="layer.type === 'video'" class="overlay-layer-placeholder"><b-icon icon="camera-video" /></div>
		<div
			v-else-if="layer.type === 'text'"
			ref="text"
			class="overlay-layer-text"
			:class="{ editing }"
			:style="textStyle"
			spellcheck="false"
			v-text="editing ? layer.props.text : (layer.props.text || 'Text')"
			@blur="onEditBlur"
			@keydown="onEditKeyDown"
		></div>
		<div v-else-if="layer.type === 'color'" class="overlay-layer-color" :style="{ background: fill, opacity: layer.props.opacity ?? 1 }"></div>
		<div v-else-if="layer.type === 'widget'" class="overlay-layer-widget">
			<div v-if="previewShared && widgetSupported" class="overlay-layer-widget-preview" :style="widgetContentStyle">
				<!-- Lays itself out at the layer's real size, as in the stream, and is scaled as a whole - so no re-flow at any zoom. -->
				<div ref="widget" class="overlay-layer-widget-scaler" :style="widgetScalerStyle"></div>
			</div>
			<div v-else class="overlay-layer-placeholder">
				<b-icon icon="grid-1x2-fill" />
				<div v-if="labels" class="small">{{widgetLabel}}</div>
			</div>
		</div>
	</div>
</template>

<script>
import { resolveBrandColor, resolveBrandDeep } from '@/overlayBrand';
import { loadGoogleFont } from '@/googleFonts';
import OverlayHost from '@/overlay/widgetHost';
import { WIDGET_MOUNTERS } from '@/overlay/widgetMounters';
import { cropContentStyle } from '@/overlayCrop';
import { ALL_WIDGET_TYPES, isMediaLayer } from './layerTypes';

const VERTICAL_ALIGNS = { top: 'flex-start', middle: 'center', bottom: 'flex-end' };

export default {
	props: {
		layer: { type: Object, required: true },
		overlay: { type: Object, required: true },
		scale: { type: Number, default: 1 },
		labels: { type: Boolean, default: true },
		// Text layers only: the text becomes editable in place (and, while focused, moveable stops dragging it).
		editing: { type: Boolean, default: false },
		// Where the editing double-click landed (client coordinates); the caret goes there. Without it, at the end.
		editPoint: { type: Object, default: null },
		// One shared services instance for every widget layer's live preview; null renders a placeholder instead.
		previewShared: { type: Object, default: null },
	},
	watch: {
		editing(editing) {
			if(editing) this.$nextTick(this.startEditing);
		},
		'layer.props.src'() { this.$nextTick(this.syncVideo); },
		'layer.props.autoplay'() { this.$nextTick(this.syncVideo); },
		'layer.props.fontFamily': {
			immediate: true,
			handler(family) { loadGoogleFont(family); },
		},
		previewShared() { this.$nextTick(this.syncWidget); },
		'layer.props.widgetType'() { this.$nextTick(this.syncWidget); },
		'layer.props.uuid'() { this.$nextTick(this.syncWidget); },
		'layer.props.settings': {
			deep: true,
			handler() { this.widgetHost?.pushSettings(this.widgetSettings()); },
		},
	},
	created() {
		this.widgetHost = null;
		this.widgetKey = null;
		this.destroyed = false;
	},
	mounted() {
		this.syncVideo();
		this.syncWidget();
	},
	beforeDestroy() {
		this.destroyed = true;
		this.widgetHost?.destroy();
	},
	methods: {
		syncVideo() {
			const el = this.$refs.video;
			if(!el) return;
			if(this.layer.props.autoplay !== false) el.play().catch(() => {});
			else el.pause();
		},
		widgetSettings() { return resolveBrandDeep(this.layer.props.settings || {}, this.overlay); },
		// Mounts the same widget class and mounter the OBS output page uses, so the editor shows a real, live
		// preview (including test alerts and chat messages) instead of a static icon.
		syncWidget() {
			if(this.layer.type !== 'widget' || !this.previewShared || !this.widgetSupported) return;
			const type = this.layer.props.widgetType;
			const key = `${type}:${this.layer.props.uuid}`;
			if(this.widgetHost && this.widgetKey === key) return;
			this.widgetHost?.destroy();
			this.widgetHost = null;
			this.widgetKey = key;
			const container = this.$refs.widget;
			if(!container) return;
			const host = new OverlayHost(this.previewShared, type, container, this.widgetSettings());
			this.widgetHost = host;
			Promise.resolve(WIDGET_MOUNTERS[type](host)).then(() => {
				if(this.destroyed || this.widgetHost !== host) host.destroy();
			}).catch(err => console.error(`Widget layer "${this.layer.name}" (${type}) failed to load`, err));
		},
		startEditing() {
			const el = this.$refs.text;
			if(!el) return;
			// Plain text only where the browser supports it, so pasted formatting and line breaks stay plain.
			el.setAttribute('contenteditable', 'plaintext-only');
			if(el.contentEditable !== 'plaintext-only') el.setAttribute('contenteditable', 'true');
			this.cancelled = false;
			this.active = true;
			el.focus();
			const range = this.getCaretRange(el);
			const selection = window.getSelection();
			selection.removeAllRanges();
			selection.addRange(range);
		},
		getCaretRange(el) {
			const point = this.editPoint;
			let range = null;
			if(point && document.caretPositionFromPoint) {
				const position = document.caretPositionFromPoint(point.x, point.y);
				if(position) {
					range = document.createRange();
					range.setStart(position.offsetNode, position.offset);
				}
			} else if(point && document.caretRangeFromPoint) {
				range = document.caretRangeFromPoint(point.x, point.y);
			}
			if(!range || !el.contains(range.startContainer)) {
				range = document.createRange();
				range.selectNodeContents(el);
				range.collapse(false);
			}
			range.collapse(true);
			return range;
		},
		onEditKeyDown(e) {
			if(!this.active) return;
			if(e.key === 'Escape') {
				this.cancelled = true;
				e.target.blur();
			} else if(e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
				e.preventDefault();
				e.target.blur();
			}
		},
		// Deliberately not gated on the `editing` prop: pressing elsewhere can clear that before the blur arrives, and the
		// text typed so far still has to be committed.
		onEditBlur(e) {
			if(!this.active) return;
			this.active = false;
			const el = e.target;
			const text = this.cancelled ? null : el.innerText;
			// Vue won't notice the typed-over DOM when nothing changed in the data, so put the saved text back by hand.
			if(this.cancelled) el.textContent = this.layer.props.text || 'Text';
			el.removeAttribute('contenteditable');
			window.getSelection().removeAllRanges();
			this.$emit('edit-end', text);
		},
	},
	computed: {
		fill() { return resolveBrandColor(this.layer.props.fill || '#888888', this.overlay); },
		widgetLabel() { return ALL_WIDGET_TYPES.find(w => w.value === this.layer.props.widgetType)?.label || this.layer.props.widgetType; },
		widgetSupported() { return !!WIDGET_MOUNTERS[this.layer.props.widgetType]; },
		mediaCropStyle() { return cropContentStyle(this.layer.props?.crop); },
		// Camera/speaker/dynamic-grid widgets crop exactly like image/video (the mounted video content just
		// fills this container); a plain overlay widget (chat, alerts, ...) is never offered the crop tool, so
		// this stays the neutral no-op box for it.
		widgetContentStyle() { return isMediaLayer(this.layer) ? cropContentStyle(this.layer.props?.crop) : {}; },
		widgetScalerStyle() {
			return { width: `calc(100% / ${this.scale})`, height: `calc(100% / ${this.scale})`, transform: `scale(${this.scale})` };
		},
		textStyle() {
			const p = this.layer.props;
			return {
				fontSize: `${(p.fontSize || 32) * this.scale}px`,
				color: resolveBrandColor(p.color || '#ffffff', this.overlay),
				fontFamily: p.fontFamily ? `"${p.fontFamily}", sans-serif` : null,
				textAlign: p.align || 'left',
				justifyContent: VERTICAL_ALIGNS[p.verticalAlign] || 'flex-start',
				fontWeight: p.bold ? 'bold' : 'normal',
				fontStyle: p.italic ? 'italic' : 'normal',
			};
		},
	},
};
</script>

<style scoped>
.overlay-layer-content,
.overlay-layer-color,
.overlay-layer-placeholder,
.overlay-layer-text,
.overlay-layer-widget,
.overlay-layer-widget-preview,
img,
video {
	width: 100%;
	height: 100%;
}

.overlay-layer-content {
	position: relative;
	/* A cropped image/video is its natural (uncropped) size, positioned so only the crop shows; this clips the rest. */
	overflow: hidden;
}

img,
video {
	position: absolute;
	display: block;
	/* Video elements default to `contain`; layers always stretch. */
	object-fit: fill;
	pointer-events: none;
}

.overlay-layer-widget-preview {
	/* A real, live widget instance — same as the OBS output. Its own DOM must never steal editor clicks/drags. */
	position: absolute;
	overflow: hidden;
	pointer-events: none;
}

.overlay-layer-widget-scaler {
	position: absolute;
	left: 0;
	top: 0;
	transform-origin: 0 0;
	overflow: hidden;
	/* Widgets are built for a bare page (widget.scss): undo the Bootstrap base this app sets on body, which they
	inherit - above all line-height (1.5 here, normal in the stream), which made chat rows taller. */
	font-family: Noto, Arial, sans-serif;
	font-size: 18px;
	font-weight: normal;
	line-height: normal;
	text-align: start;
	color: initial;
}

.overlay-layer-placeholder {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	background: rgba(255,255,255,.1);
	color: #fff;
	overflow: hidden;
}

.overlay-layer-text.editing {
	cursor: text;
	outline: none;
}

.overlay-layer-text {
	display: flex;
	flex-direction: column;
	overflow: hidden;
	white-space: pre-wrap;
	line-height: 1.2;
}
</style>

<!-- Not scoped: it has to reach the widget's own elements. :where() keeps every selector at Bootstrap's own specificity, so
     these win only by coming later - and any class rule a widget sets itself still wins over them. -->
<style>
/* The app's Bootstrap (Reboot) restyles bare elements, which the bare page a widget is built for (and the stream) never had.
   Put the browser's own defaults back inside a widget preview. */
:where(.overlay-layer-widget-scaler) *,
:where(.overlay-layer-widget-scaler) *::before,
:where(.overlay-layer-widget-scaler) *::after {
	box-sizing: content-box;
}

:where(.overlay-layer-widget-scaler) img,
:where(.overlay-layer-widget-scaler) svg {
	vertical-align: baseline;
}

:where(.overlay-layer-widget-scaler) small {
	font-size: smaller;
	font-weight: inherit;
}

:where(.overlay-layer-widget-scaler) b,
:where(.overlay-layer-widget-scaler) strong {
	font-weight: bold;
}

:where(.overlay-layer-widget-scaler) a {
	color: LinkText;
	text-decoration: underline;
}

:where(.overlay-layer-widget-scaler) p {
	margin: 1em 0;
}

:where(.overlay-layer-widget-scaler) ul,
:where(.overlay-layer-widget-scaler) ol {
	margin: 1em 0;
}
</style>
